/**
 * Display vocabulary for the session settings: what a model and an effort
 * level are CALLED. Shared by the composer's pickers, the chips, and the
 * Settings/product-settings pages — one copy, same words everywhere.
 *
 * Round 7 (owner: "I want Conductor style for model picker. Right now the
 * dropdown picker looks ugly") added the two the picker needs: the spoken
 * model name ("Opus 4.8", not "claude-opus-4-8 ($5/$25 per M tokens)") and
 * the family grouping the popover lays its rows out in.
 *
 * The four teaching preferences left with round 2 (owner: "Plain English
 * should not be an option but always be applied... Same as code details").
 * They are permanent server-side behavior now, so there is no picker, no
 * switch and no vocabulary for them here.
 */

import type { Effort } from '../../api/types';
import { PICKER_MODELS, priceForModel } from './pricing';

/**
 * What "Default" resolves to when neither the product nor the account
 * carries one (the server's own default). Shared so the home composer and a
 * session composer never disagree about what the fallback is.
 */
export const FALLBACK_MODEL = 'claude-fable-5-1'; // the Best tier (owner's call 2026-09-01, DECISIONS #81b)
export const FALLBACK_EFFORT: Effort = 'medium';

/**
 * The recommended tiers (2026-09-01, after the model bench; owner: "should we
 * offer users option to choose model because it's BYOK... or focus on
 * bringing the best experience where we optimize and pick models?" — both:
 * an opinionated default, the choice kept as tiers rather than SKUs). Each
 * tier is a model the bench passed, with what it buys and roughly what one
 * answer costs on a ~300-node codebase (bench averages, cache-aware list
 * price; a real answer varies with how much the guide has to read).
 * Re-benched each model release; the raw list stays under "All models".
 */
export interface ModelTier {
  key: 'best' | 'balanced' | 'budget';
  label: string;
  model: string;
  /** the effort the bench measured this tier at: a tier is a model AND a
   * setting (Sonnet at medium out-grounded Sonnet at high, 35% faster) */
  effort: Effort;
  /** what this tier buys, in one clause */
  why: string;
  /** bench average, USD per answer */
  perAnswerUsd: number;
}

export const MODEL_TIERS: readonly ModelTier[] = [
  { key: 'best', label: 'Best', model: 'claude-fable-5-1', effort: 'high', why: 'the newest and most thorough', perAnswerUsd: 1.4 },
  { key: 'balanced', label: 'Balanced', model: 'claude-opus-5', effort: 'high', why: 'most complete, best pictures', perAnswerUsd: 0.7 },
  { key: 'budget', label: 'Budget', model: 'claude-sonnet-5', effort: 'medium', why: 'fastest and cheapest; slips into code names', perAnswerUsd: 0.17 },
];

/** "≈ $0.70 per answer" — the tier row's right-hand line. */
export function tierCostNote(tier: ModelTier): string {
  return `\u2248 $${tier.perAnswerUsd.toFixed(2)} per answer`;
}

/** Wire value → display word. Object order is the picker order (low → max). */
export const EFFORT_LABELS: Record<Effort, string> = {
  low: 'low',
  medium: 'medium',
  high: 'high',
  xhigh: 'extra high',
  max: 'max',
};

/** The five levels, weakest to strongest. Order is the meter's order. */
export const EFFORT_ORDER: readonly Effort[] = ['low', 'medium', 'high', 'xhigh', 'max'];

/** "xhigh" → "Extra high": the word a control puts beside its bars. */
export function effortDisplayName(effort: Effort): string {
  const word = EFFORT_LABELS[effort];
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * "claude-opus-4-8" → "Opus 4.8": the name a person would say out loud, for
 * the picker rows and the composer's footer.
 *
 * Only ids of the shape `claude-<family>-<n>(-<n>)*` are rewritten. Anything
 * else passes through VERBATIM rather than being guessed at: an id we cannot
 * read is better shown as it is than prettied into a name nobody chose.
 */
export function modelDisplayName(model: string): string {
  if (!model.startsWith('claude-')) return model;
  const parts = model.slice('claude-'.length).split('-');
  const [family, ...version] = parts;
  if (family === undefined || family.length === 0) return model;
  if (version.length === 0 || !version.every((p) => /^\d+$/.test(p))) return model;
  return `${family.charAt(0).toUpperCase()}${family.slice(1)} ${version.join('.')}`;
}

/** The family an id belongs to: "claude-opus-4-8" → "opus". */
export function modelFamily(model: string): string {
  if (!model.startsWith('claude-')) return model;
  return model.slice('claude-'.length).split('-')[0] ?? model;
}

/**
 * One heading and the models under it in the picker.
 *
 * `note` is the family's list price, and it is present ONLY when every model
 * in the group prices the same (they all do today). A family whose members
 * disagree carries no note rather than a number that is true of some of them.
 */
export interface ModelGroup {
  /** stable key for tests and React keys */
  key: string;
  /** the visible heading (this IS the copy) */
  label: string;
  /** the shared list price, when the family has one */
  note: string | null;
  models: readonly string[];
}

/** "$5 / $25 per M tokens", or null for a model with no price on file. */
function priceNote(model: string): string | null {
  const price = priceForModel(model);
  if (price === null) return null;
  return `$${price.inputPerMTok} / $${price.outputPerMTok} per M tokens`;
}

/**
 * The picker's list, grouped by model FAMILY (Fable, Opus, Sonnet) in
 * PICKER_MODELS order (the current generation; older ids validate but are
 * not offered).
 *
 * The reference this picker copies groups by provider; we have exactly one
 * provider, so the honest analogue is the family, which is the real thing
 * behind both the capability step and the price. Grouping also lets the
 * price be said once per family instead of on all eight rows, which is what
 * made the old dropdown a wall of parentheses.
 *
 * `extra` is a model the caller is actually on that the list does not carry
 * (a legacy session row): it joins the end under its own heading, so the
 * current selection always has a row to be ticked on.
 */
export function modelGroups(extra?: string | null): ModelGroup[] {
  const ids = [...PICKER_MODELS];
  if (extra !== undefined && extra !== null && !ids.includes(extra)) ids.push(extra);

  const groups: ModelGroup[] = [];
  for (const id of ids) {
    const key = modelFamily(id);
    const existing = groups.find((g) => g.key === key);
    if (existing === undefined) {
      groups.push({
        key,
        label: `${key.charAt(0).toUpperCase()}${key.slice(1)}`,
        note: priceNote(id),
        models: [id],
      });
      continue;
    }
    existing.models = [...existing.models, id];
    if (existing.note !== priceNote(id)) existing.note = null;
  }
  return groups;
}
